import {
  CONDITION_STATUS,
  TIME_OF_YEAR,
  getBikeValue,
  getConditionFactor,
  getLegendFactor,
  getSeasonFactor,
  getTimeFactor,
} from "src/components/Calculator/bikeValue";
import {
  CalculatorBodyText,
  CalculatorHeadline,
  CalculatorTopicH2Headline,
  CalculatorTopicHeadline,
  PreCalculatorHeadline,
} from "src/components/Calculator/CalculatorTypography";
import { FC, useState } from "react";
import { MenuItem, makeStyles } from "@material-ui/core";

import BRANDS from "src/components/Calculator/brands";
import { CalculatorButton } from "src/components/Calculator/components/CalculatorButton";
import { CalculatorButtonLinkCentered } from "src/components/Calculator/components/CalculatorButtonLinkCentered";
import { CalculatorImage } from "src/components/Calculator/CalculatorImage";
import CalculatorInputSection from "src/components/Calculator/CalculatorInputSection";
import { CalculatorLayout } from "src/components/Calculator/CalculatorLayout";
import { CalculatorResultsTitle } from "src/components/Calculator/components/CalculatorResultsTitle";
import CycleverseDivider from "src/components/CycleverseDivider";
import { DisplayResult } from "src/components/Calculator/components/DisplayResult";
import LocaleLink from "src/components/Navigation/LocaleLink";
import SeoHead from "src/components/Seo/SeoHead";
import { TheCycleverseTheme } from "src/theme";
import useTranslation from "next-translate/useTranslation";

const useStyles = makeStyles((theme: TheCycleverseTheme) => ({
  button: {
    backgroundColor: theme.buttonBackgroundColor,
    color: theme.buttonColor,
    "&:hover": {
      backgroundColor: theme.buttonBackgroundColor,
    },
    marginTop: "20px",
  },
  alignCenter: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
  },
  bottomContainer: {
    paddingTop: "30px",
  },
  bottomTitle: {
    fontFamily: "Roboto 300",
    fontSize: "16px",
    lineHeight: "23px",
    fontWeight: 900,
  },
  amount: {
    color: theme.amountColor,
  },
  link: {
    textDecoration: "none",
  },
  buttonLink: {},
  errorMessage: {
    fontSize: "16px",
    color: "#fd4901",
    textAlign: "center",
    width: "75%",
    margin: "auto",
    marginTop: "10px",
    "@media (max-width: 488px)": {
      width: "100%",
    },
  },
}));

const BicycleResidualValueCalculator: FC = () => {
  const { t, lang } = useTranslation("bicycleResidualValue");
  const classes = useStyles();
  const [values, setValues] = useState({
    age: null,
    manufacturerBrand: null,
    isCovetedCollector: "nein",
    condition: "",
    time: "",
    factoryPrice: null,
    myBikeWorth: 0,
    hasError: false,
  });

  const handleOnChange = (name, e) => {
    const value = name === "myBikeWorth" ? e : e?.target?.value;
    const onlyNumbersRegexDE = /^[0-9]*([,][0-9]*)?$/;
    const onlyNumbersRegexEN = /^[0-9]*([.][0-9]*)?$/;
    if (name === "factoryPrice" && lang === "de" && onlyNumbersRegexDE.test(value)) {
      setValues((el) => ({
        ...el,
        [name]: value,
      }));
    } else if (name === "factoryPrice" && lang === "en" && onlyNumbersRegexEN.test(value)) {
      setValues((el) => ({
        ...el,
        [name]: value,
      }));
    } else if (name !== "factoryPrice") {
      setValues((el) => ({
        ...el,
        [name]: name !== "manufacturerBrand" ? value : BRANDS[value],
      }));
    }
  };

  const handleCalculate = () => {
    if (
      values.age &&
      values.manufacturerBrand &&
      values.isCovetedCollector &&
      values.condition &&
      values.time &&
      values.factoryPrice
    ) {
      const myBikeWorth = getBikeValue(
        Number(values?.factoryPrice?.replace(",", ".")),
        getTimeFactor(values?.manufacturerBrand, values?.age, Number(values?.factoryPrice?.replace(",", "."))),
        getConditionFactor(values?.condition),
        getLegendFactor(values?.isCovetedCollector),
        getSeasonFactor(values?.time)
      );
      handleOnChange("myBikeWorth", Number(myBikeWorth?.toFixed(2))?.toLocaleString(lang));
      setValues((el) => ({
        ...el,
        hasError: false,
      }));
    } else {
      setValues((el) => ({
        ...el,
        hasError: true,
      }));
    }
  };

  return (
    <CalculatorLayout
      header={
        <div data-test="page-rechner-fahrrad-restwert-rechner">
          <PreCalculatorHeadline>{t("calculatorBicycleResidualValueHeadPreline")}</PreCalculatorHeadline>
          <CalculatorHeadline>
            {t("calculatorBicycleResidualValueHeadline1")}
            <br />
            {t("calculatorBicycleResidualValueHeadline2")}
          </CalculatorHeadline>
        </div>
      }
    >
      <SeoHead
        title={t("barPsiSeoTitle")}
        description={t("barPsiSeoDescription")}
        ogImage="/images/calculators/The-Cycleverse-OpenGraph-Restwertrechner.png"
        ogDescription={t("openGraphDescription")}
        ogTitle={t("psiOpenGraphTitel")}
      />
      <CalculatorImage
        src="/images/calculators/Fahrrad-Gebrauchtwert-Rechner-Header.svg"
        width={400}
        height={263}
        layout="intrinsic"
        alt={t("calculatorBicycleText18")}
      />
      <CalculatorButton text={t("calculatorBicycleResidualButton")} scroll idToScrollTo="calculator" />

      <CalculatorBodyText>{t("calculatorBicycleWorthText")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorBicycleWorthAnswerText")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorBicycleWorthAnswerText2")}</CalculatorBodyText>
      <div className={classes.alignCenter} id="calculator">
        <CalculatorTopicH2Headline>
          {t("calculatorBicycleText1")}
          <br /> {t("calculatorBicycleText2")}
          <br /> {t("calculatorBicycleText3")}
        </CalculatorTopicH2Headline>
      </div>

      <CalculatorInputSection
        inputTitle={t("calculatorBicycleInputText1")}
        inputUnite="Euro"
        inputNumber="1"
        onChange={(e) => handleOnChange("factoryPrice", e)}
        value={values?.factoryPrice}
      />
      <CalculatorInputSection
        inputTitle={t("calculatorBicycleInputText2")}
        inputUnite={t("age")}
        inputNumber="2"
        isSelect
        onChange={(e) => handleOnChange("age", e)}
        value={values?.age}
      >
        {Array.from({ length: 20 }, (_, i) => i + 1)?.map((el) => (
          <MenuItem key={el} value={el}>
            {el}
          </MenuItem>
        ))}
      </CalculatorInputSection>
      <CalculatorInputSection
        inputTitle={t("calculatorBicycleInputText3")}
        inputCaption={t("calculatorBicycleCaptionText3")}
        inputNumber="3"
        isSelect
        onChange={(e) => handleOnChange("manufacturerBrand", e)}
        value={values?.manufacturerBrand?.index?.toString()}
      >
        {BRANDS?.map((el) => (
          <MenuItem key={el.index} value={el.index?.toString()}>
            {el.brand}
          </MenuItem>
        ))}
      </CalculatorInputSection>
      <CalculatorInputSection
        inputTitle={t("calculatorBicycleInputText4")}
        inputNumber="4"
        isSelect
        onChange={(e) => handleOnChange("isCovetedCollector", e)}
        value={values?.isCovetedCollector}
      >
        {["ja", "nein"]?.map((el) => (
          <MenuItem key={el} value={el}>
            {el}
          </MenuItem>
        ))}
      </CalculatorInputSection>
      {values?.isCovetedCollector === "ja" && <CalculatorBodyText>{t("calculatorBicycleText17")}</CalculatorBodyText>}
      <CalculatorInputSection
        inputTitle={t("calculatorBicycleInputText5")}
        inputNumber="5"
        isSelect
        onChange={(e) => handleOnChange("condition", e)}
        value={values?.condition}
      >
        {CONDITION_STATUS?.map((el) => (
          <MenuItem key={el} value={el}>
            {el}
          </MenuItem>
        ))}
      </CalculatorInputSection>
      <CalculatorInputSection
        inputTitle={t("calculatorBicycleInputText6")}
        inputNumber="6"
        isSelect
        onChange={(e) => handleOnChange("time", e)}
        value={values?.time}
      >
        {TIME_OF_YEAR?.map((el) => (
          <MenuItem key={el} value={el}>
            {el}
          </MenuItem>
        ))}
      </CalculatorInputSection>
      {values?.hasError ? <p className={classes.errorMessage}>{t("calculatorBicycleTextError")}</p> : null}

      <CalculatorButton text={t("calculatorBicycleResidualButton2")} onClick={handleCalculate} />

      <CalculatorResultsTitle text={t("calculatorBicycleText4")} />
      <DisplayResult
        text1={t("calculatorBicycleText5")}
        result={`${values?.myBikeWorth} ${t("calculatorBicycleText6")}`}
        className={classes.amount}
      />

      <CycleverseDivider marginTop={4} />

      <CalculatorTopicH2Headline>{t("calculatorBicycleText7")}</CalculatorTopicH2Headline>
      <CalculatorBodyText>{t("calculatorBicycleText8")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorBicycleText9")}</CalculatorBodyText>
      <CalculatorBodyText>{t("calculatorBicycleText10")}</CalculatorBodyText>

      <CalculatorBodyText>
        <LocaleLink href={`/blog/was-ist-mein-fahrrad-wert`} as={`/blog/was-ist-mein-fahrrad-wert`} target="new">
          {t("calculatorBicycleText11")}
        </LocaleLink>{" "}
      </CalculatorBodyText>

      <CalculatorBodyText>{t("calculatorBicycleText12")}</CalculatorBodyText>
      <CalculatorBodyText>
        {t("calculatorBicycleText13")}{" "}
        <LocaleLink href={`/blog/fahrrad-verkaufen-tipps`} as={`/blog/fahrrad-verkaufen-tipps`} target="new">
          {t("calculatorBicycleText14")}
        </LocaleLink>{" "}
      </CalculatorBodyText>
      <CalculatorTopicHeadline>{t("calculatorBicycleText15")}</CalculatorTopicHeadline>

      <CalculatorButtonLinkCentered href="/fahrraeder" text={t("calculatorBicycleText16")} />
    </CalculatorLayout>
  );
};

export default BicycleResidualValueCalculator;
